import { createContext, useContext, useEffect, useState, useMemo, ReactNode } from "react";
import { useParams, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { slugify } from "@/lib/slugify";

interface Tenant {
  id: string;
  name: string;
  slug: string | null;
  logo_url: string | null;
  is_active: boolean;
}

interface TenantContextType {
  tenant: Tenant | null;
  tenantId: string | null;
  loading: boolean;
}

interface StorefrontTenantRow {
  tenant_id: string | null;
  store_name: string;
  public_name: string | null;
  logo_url: string | null;
  subdomain: string | null;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

function mapStorefrontTenant(row: StorefrontTenantRow | null): Tenant | null {
  if (!row?.tenant_id) return null;

  const resolvedName = row.public_name?.trim() || row.store_name;
  const resolvedSlug = row.subdomain?.trim() || slugify(resolvedName);

  return {
    id: row.tenant_id,
    name: resolvedName,
    slug: resolvedSlug || null,
    logo_url: row.logo_url,
    is_active: true,
  };
}

async function resolveTenantBySlug(slug: string): Promise<Tenant | null> {
  const { data: directMatch } = await supabase
    .from("store_settings")
    .select("tenant_id, store_name, public_name, logo_url, subdomain")
    .eq("subdomain", slug)
    .limit(1)
    .maybeSingle();

  if (directMatch) {
    return mapStorefrontTenant(directMatch as StorefrontTenantRow);
  }

  const { data: candidates } = await supabase
    .from("store_settings")
    .select("tenant_id, store_name, public_name, logo_url, subdomain");

  const matched = (candidates ?? []).find((row) => {
    const possibleSlugs = [row.subdomain, row.public_name, row.store_name]
      .filter(Boolean)
      .map((value) => slugify(String(value)));

    return possibleSlugs.includes(slug);
  });

  return mapStorefrontTenant((matched as StorefrontTenantRow | undefined) ?? null);
}

async function resolveTenantByMembership(userId: string): Promise<Tenant | null> {
  const { data: membership } = await supabase
    .from("tenant_members")
    .select("tenant_id")
    .eq("user_id", userId)
    .limit(1)
    .maybeSingle();

  if (!membership?.tenant_id) return null;

  const { data: settings } = await supabase
    .from("store_settings")
    .select("tenant_id, store_name, public_name, logo_url, subdomain")
    .eq("tenant_id", membership.tenant_id)
    .limit(1)
    .maybeSingle();

  return mapStorefrontTenant((settings as StorefrontTenantRow | null) ?? null);
}

/**
 * Inner provider that resolves tenant from:
 * 1. URL slug (/loja/:slug) — for anonymous storefront browsing
 * 2. User membership (tenant_members) — for authenticated users
 * URL slug takes priority when present.
 */
function TenantProviderInner({ children, slugOverride }: { children: ReactNode; slugOverride?: string }) {
  const { user } = useAuth();
  const params = useParams<{ slug?: string }>();
  const location = useLocation();

  const slugFromPath = useMemo(() => {
    const match = location.pathname.match(/^\/loja\/([^/]+)/);
    return match ? match[1] : undefined;
  }, [location.pathname]);

  const slug = slugOverride || params.slug || slugFromPath;
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      setLoading(true);

      const resolvedTenant = slug
        ? await resolveTenantBySlug(slug)
        : user
          ? await resolveTenantByMembership(user.id)
          : null;

      if (!cancelled) {
        setTenant(resolvedTenant);
        setLoading(false);
      }
    }

    resolve();

    return () => {
      cancelled = true;
    };
  }, [user, slug]);

  const value = useMemo<TenantContextType>(
    () => ({ tenant, tenantId: tenant?.id ?? null, loading }),
    [tenant, loading]
  );

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function TenantProvider({ children, slug }: { children: ReactNode; slug?: string }) {
  return <TenantProviderInner slugOverride={slug}>{children}</TenantProviderInner>;
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) throw new Error("useTenant must be used within TenantProvider");
  return context;
}
